import { Parser, annotationParamKind } from "../parser/parser.js";
import { Lexer } from "../lexer/lexer.js";
import { TokenType } from "../types/tokens.js";
import type { Token } from "../types/tokens.js";
import { quoteId } from "./quote-id.js";
import { quoteString, emitDescription } from "./quote-string.js";
import type {
  KrsFile,
  KrsNode,
  KrsEdge,
  DeployBlock,
  DeployNode,
  ImportDeclaration,
  OrganizationBlock,
  TeamNode,
  MemberNode,
  LinkEntry,
  BoundaryBlock,
  FacetBlock,
  LegendBlock,
  LegendEntry,
  LegendRefTarget,
} from "../types/ast.js";
import { edgeArrow } from "../types/ast.js";

export class FormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FormatError";
  }
}

/**
 * Format a .krs source string.
 *
 * - Normalises indentation (2 spaces), blank lines, and spacing.
 * - Preserves line comments (// ...) and block comments (/* ... *\/).
 * - Throws FormatError when the source has parse errors.
 * - Idempotent: format(format(src)) === format(src).
 *
 * Known limitation (v1): comments that appear between properties inside a
 * block body may be relocated to immediately before the next child node or
 * edge, because property positions are not stored in the AST.
 */
export function format(src: string): string {
  const parseResult = Parser.parse(src);
  if (parseResult.diagnostics.some((d) => d.severity === "error")) {
    throw new FormatError("Cannot format: source contains parse errors");
  }
  const commentTokens = new Lexer(src)
    .tokenizeWithComments()
    .filter((t) => t.type === TokenType.LineComment || t.type === TokenType.BlockComment);
  return new Printer(commentTokens).printFile(parseResult.value);
}

/**
 * Serialize an already-parsed (or merged) {@link KrsFile} AST back to `.krs`
 * source text. Unlike {@link format}, which parses a source string first, this
 * takes an AST directly — used to flatten a multi-file project (resolved by
 * `ImportResolver`) into a single self-contained `.krs` for inline sharing.
 *
 * Comments are not preserved (no source/token stream is available for a merged
 * AST); structural content round-trips faithfully.
 */
export function serializeKrsFile(file: KrsFile): string {
  return new Printer([]).printFile(file);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function kindToKeyword(kind: string): string {
  // queue-item uses "queue" keyword in source
  return kind === "queue-item" ? "queue" : kind;
}

type HasLoc = { loc: { start: { line: number }; end: { line: number } } };

// ---------------------------------------------------------------------------
// Printer
// ---------------------------------------------------------------------------

class Printer {
  /** Comment tokens indexed by source line (1-based). */
  private readonly byLine: Map<number, Token[]>;
  /** Lines whose comment tokens have already been emitted. */
  private readonly emitted: Set<number>;

  constructor(commentTokens: Token[]) {
    this.byLine = new Map();
    this.emitted = new Set();
    for (const tok of commentTokens) {
      const bucket = this.byLine.get(tok.loc.line) ?? [];
      bucket.push(tok);
      this.byLine.set(tok.loc.line, bucket);
    }
  }

  // ── Public entry point ────────────────────────────────────────────────────

  printFile(file: KrsFile): string {
    const out: string[] = [];
    let prevEndLine = 0;

    // @import (style imports — no loc available)
    for (const path of file.styleImports) {
      out.push(`@import ${quoteString(path)}`);
    }

    // import (node imports — have loc)
    for (const imp of file.nodeImports) {
      const leadingToks = this.extractLeading(prevEndLine + 1, imp.loc.start.line - 1);
      out.push(...this.renderLeading(leadingToks, ""));
      const trail = this.extractTrailing(imp.loc.start.line);
      out.push(this.renderImport(imp) + trail);
      prevEndLine = imp.loc.end.line;
    }

    // Top-level blocks sorted by source line.
    //
    // This list must enumerate **every** block-valued top-level array on
    // `KrsFile`. An array omitted here is silently dropped from the output —
    // `fmt` then destroys source the parser accepted (#2076). The exhaustiveness
    // guard in `formatter-top-level-coverage.test.ts` fails when a new top-level
    // array is added to `KrsFile` without being wired in here.
    const topLevel = [
      ...file.systems,
      ...file.services,
      ...file.clients,
      ...file.domains,
      ...file.databases,
      ...file.queues,
      ...file.storages,
      ...file.deploys,
      ...file.organizations,
      ...file.boundaries,
      ...file.facets,
      ...file.legends,
    ].sort((a, b) => a.loc.start.line - b.loc.start.line);

    for (const block of topLevel) {
      const startLine = block.loc.start.line;
      const leadingToks = this.extractLeading(prevEndLine + 1, startLine - 1);

      if (out.length > 0) out.push(""); // blank line between top-level blocks
      out.push(...this.renderLeading(leadingToks, ""));
      out.push(...this.renderTopLevel(block));
      prevEndLine = block.loc.end.line;
    }

    // Remaining comments (e.g. file footer)
    const remaining = this.remainingComments();
    if (remaining.length > 0) {
      if (out.length > 0) out.push("");
      out.push(...this.renderLeading(remaining, ""));
    }

    return out.join("\n") + "\n";
  }

  // ── Comment helpers ───────────────────────────────────────────────────────

  /** Extract not-yet-emitted comments between source lines [from, to]. */
  private extractLeading(from: number, to: number): Token[] {
    const result: Token[] = [];
    for (let line = from; line <= to; line++) {
      if (this.emitted.has(line)) continue;
      const toks = this.byLine.get(line);
      if (toks) {
        result.push(...toks);
        this.emitted.add(line);
      }
    }
    return result;
  }

  /**
   * Extract a trailing comment on a specific line (same line as a declaration).
   * Returns a formatted string like " // comment" or "" if none.
   */
  private extractTrailing(line: number): string {
    if (this.emitted.has(line)) return "";
    const toks = this.byLine.get(line);
    if (!toks || toks.length === 0) return "";
    this.emitted.add(line);
    return " " + toks.map((t) => this.renderCommentToken(t)).join(" ");
  }

  /** Collect all remaining (not yet emitted) comment tokens in source order. */
  private remainingComments(): Token[] {
    const result: Token[] = [];
    const sortedLines = [...this.byLine.keys()].sort((a, b) => a - b);
    for (const line of sortedLines) {
      if (this.emitted.has(line)) continue;
      result.push(...(this.byLine.get(line) ?? []));
      this.emitted.add(line);
    }
    return result;
  }

  private renderLeading(toks: Token[], indent: string): string[] {
    return toks.map((t) => indent + this.renderCommentToken(t));
  }

  private renderCommentToken(tok: Token): string {
    if (tok.type === TokenType.LineComment) return `// ${tok.value}`;
    return `/* ${tok.value} */`;
  }

  // ── Import ────────────────────────────────────────────────────────────────

  private renderImport(imp: ImportDeclaration): string {
    if (imp.ids.length === 0) return `import ${quoteString(imp.path)}`;
    // Bare ids are stored as `["Foo"]`; multi-segment paths as `["A", "B", "C"]`.
    // Re-join each path with "." to round-trip through the formatter.
    const formatted = imp.ids.map((entry) => entry.path.map(quoteId).join(".")).join(", ");
    return `import { ${formatted} } from ${quoteString(imp.path)}`;
  }

  // ── Top-level dispatch ────────────────────────────────────────────────────

  private renderTopLevel(block: HasLoc): string[] {
    if ("nodes" in block) return this.renderDeployBlock(block as DeployBlock);
    if ("teams" in block) return this.renderOrganizationBlock(block as OrganizationBlock);
    if ("contains" in block) return this.renderBoundaryBlock(block as BoundaryBlock);
    // A `facet` block has no distinguishing member array to duck-type on (its
    // whole point is having no membership list), so dispatch on the tag.
    if ("kind" in block && (block as { kind: unknown }).kind === "facet") {
      return this.renderFacetBlock(block as FacetBlock);
    }
    if ("entries" in block) return this.renderLegendBlock(block as LegendBlock);
    // Everything else is a KrsNode: system / service / client / domain and the
    // infra kinds (database / queue / storage), which are legal at top level
    // (ADR-702) and render exactly as they do when nested.
    return this.renderNode(block as KrsNode, 0);
  }

  // ── KrsNode ───────────────────────────────────────────────────────────────

  private renderNode(node: KrsNode, depth: number): string[] {
    const indent = "  ".repeat(depth);
    const keyword = kindToKeyword(node.kind);

    // Declaration parts: keyword id [tags] @annotations
    // Resource dot-notation refs serialise each segment separately so the
    // joined id (which contains a `.`) does not get wrapped in quotes.
    const idText =
      node.kind === "resource" && node.ref
        ? `${quoteId(node.ref.parent)}.${quoteId(node.ref.child)}`
        : quoteId(node.id);
    const decl: string[] = [keyword, idText];
    if (node.tags.length > 0) decl.push(`[${node.tags.join(", ")}]`);
    decl.push(...renderAnnotations(node.annotations, node.annotationParams));

    const propLines = this.renderProperties(node, indent + "  ");
    const hasProps = propLines.length > 0;
    const hasChildren = node.children.length > 0;
    const hasEdges = node.edges.length > 0;

    if (!hasProps && !hasChildren && !hasEdges) {
      const trail = this.extractTrailing(node.loc.start.line);
      return [`${indent}${decl.join(" ")} {}${trail}`];
    }

    const trail = this.extractTrailing(node.loc.start.line);
    const lines: string[] = [`${indent}${decl.join(" ")} {${trail}`];
    lines.push(...propLines);

    // Children and edges sorted by source line
    type Item =
      | { kind: "child"; node: KrsNode; line: number; endLine: number }
      | { kind: "edge"; edge: KrsEdge; line: number; endLine: number }
      | { kind: "boundary"; block: BoundaryBlock; line: number; endLine: number };

    const items: Item[] = [
      ...node.children.map((n) => ({
        kind: "child" as const,
        node: n,
        line: n.loc.start.line,
        endLine: n.loc.end.line,
      })),
      ...node.edges.map((e) => ({
        kind: "edge" as const,
        edge: e,
        line: e.loc.start.line,
        endLine: e.loc.end.line,
      })),
      // Scoped `boundary` blocks (#2036). They join the same line-ordered list
      // as children and edges so authored order round-trips; omitting them here
      // would make `fmt` delete them silently, the failure ADR-2076 fixed for
      // the top-level form (TPL-1101).
      ...(node.boundaries ?? []).map((b) => ({
        kind: "boundary" as const,
        block: b,
        line: b.loc.start.line,
        endLine: b.loc.end.line,
      })),
    ].sort((a, b) => a.line - b.line);

    let prevEndLine = node.loc.start.line;
    let prevItemKind: "property" | "child" | "edge" | "boundary" | null = hasProps
      ? "property"
      : null;

    for (const item of items) {
      const leadingToks = this.extractLeading(prevEndLine + 1, item.line - 1);

      // Blank line rules:
      // - before each child block (always, unless it's the very first item and no properties)
      // - before first edge when preceded by properties or children
      // - no blank line between consecutive edges
      const needsBlank =
        item.kind === "child" || item.kind === "boundary"
          ? prevItemKind !== null // blank before child (except if very first with no props)
          : prevItemKind !== null && prevItemKind !== "edge"; // blank before first edge only

      if (needsBlank) lines.push("");
      lines.push(...this.renderLeading(leadingToks, indent + "  "));

      if (item.kind === "child") {
        lines.push(...this.renderNode(item.node, depth + 1));
      } else if (item.kind === "boundary") {
        lines.push(...this.renderBoundaryBlock(item.block).map((l) => `${indent}  ${l}`));
      } else {
        const edgeTrail = this.extractTrailing(item.edge.loc.start.line);
        // The trailing comment belongs to the declaration line, which for the
        // block form is the line that opens the block.
        const edgeLines = this.renderEdge(item.edge, node.kind);
        edgeLines[0] += edgeTrail;
        lines.push(...edgeLines.map((l) => `${indent}  ${l}`));
      }

      prevEndLine = item.endLine;
      prevItemKind = item.kind;
    }

    lines.push(`${indent}}`);
    return lines;
  }

  private renderProperties(node: KrsNode, indent: string): string[] {
    const lines: string[] = [];
    if (node.label !== undefined) lines.push(`${indent}label ${quoteString(node.label)}`);
    if (node.properties.description !== undefined) {
      lines.push(this.renderDescription(node.properties.description, indent));
    }
    if (node.kind === "entity" && node.tableRef) {
      lines.push(`${indent}table ${quoteId(node.tableRef.parent)}.${quoteId(node.tableRef.child)}`);
    }
    if ("role" in node.properties && node.properties.role !== undefined) {
      lines.push(`${indent}role ${quoteString(node.properties.role)}`);
    }
    // `handles` was missing here until #2549, so `karasu fmt` deleted every
    // handles line it formatted — silently, since nothing else carries the
    // one-hop expose model. Canonicalized to a single comma list like `facets`
    // below, so `format(format(x)) === format(x)` holds for repeated lines.
    // Path refs print as written, each segment re-quoted only when needed
    // (TPL-1101, #2088).
    if (
      "handles" in node.properties &&
      Array.isArray(node.properties.handles) &&
      node.properties.handles.length > 0
    ) {
      const refs = node.properties.handles.map((h) => h.path.map(quoteId).join("."));
      lines.push(`${indent}handles ${refs.join(", ")}`);
    }
    if (
      "delivers" in node.properties &&
      Array.isArray(node.properties.delivers) &&
      node.properties.delivers.length > 0
    ) {
      lines.push(`${indent}delivers ${node.properties.delivers.map(quoteId).join(", ")}`);
    }
    if (
      "operations" in node.properties &&
      Array.isArray(node.properties.operations) &&
      node.properties.operations.length > 0
    ) {
      lines.push(`${indent}operations ${this.renderOperations(node.properties.operations)}`);
    }
    // Canonicalized to a single comma list, whatever the author wrote: repeated
    // `facets` lines and duplicate ids were already collapsed at parse time, so
    // emitting one line is what makes `format(format(x)) === format(x)` hold.
    if (node.facets !== undefined && node.facets.length > 0) {
      lines.push(`${indent}facets ${node.facets.map(quoteId).join(", ")}`);
    }
    for (const link of node.properties.links) {
      lines.push(this.renderLink(link, indent));
    }
    return lines;
  }

  private renderOperations(
    ops: readonly { verb: string; decoratedAs?: readonly string[] }[],
  ): string {
    return ops
      .map((op) => {
        if (op.decoratedAs && op.decoratedAs.length > 0) {
          return `${quoteId(op.verb)}:${op.decoratedAs.join(",")}`;
        }
        return quoteId(op.verb);
      })
      .join(", ");
  }

  private renderDescription(value: string, indent: string): string {
    return emitDescription(value, indent);
  }

  private renderLink(link: LinkEntry, indent: string): string {
    if (link.label !== undefined)
      return `${indent}link ${quoteString(link.url)} ${quoteString(link.label)}`;
    return `${indent}link ${quoteString(link.url)}`;
  }

  /**
   * One edge, canonicalized. The condition is a single one (#2543, #2544):
   * **does the edge carry anything the shorthand cannot express** — a
   * `description`, a `link`, or `facets`. If not, it folds to
   * `A -> B "label"`; if so, it emits the block
   * with `label` inside it. A label-only block is therefore not a stable form,
   * which is what keeps the two spellings from becoming a dual representation
   * of one fact (TPL-1415, TPL-2542).
   */
  private renderEdge(edge: KrsEdge, parentKind?: string): string[] {
    const arrow = edgeArrow(edge.kind);
    const tags = edge.tags.length > 0 ? ` [${edge.tags.join(", ")}]` : "";
    // The author's `#<id>` is data, not decoration. Dropping it here silently
    // deleted an `edge#<id>` style selector's target (#2543).
    const id = edge.authorId !== undefined ? ` #${edge.authorId}` : "";
    // Use implicit-source shorthand for service/domain blocks (from is always parentId)
    const from =
      parentKind === "service" || parentKind === "domain" ? "" : `${quoteId(edge.from)} `;
    const head = `${from}${arrow} ${quoteId(edge.to)}`;

    const hasBlockOnlyProperty =
      edge.description !== undefined ||
      (edge.links !== undefined && edge.links.length > 0) ||
      (edge.facets !== undefined && edge.facets.length > 0);
    if (!hasBlockOnlyProperty) {
      const label = edge.label !== undefined ? ` ${quoteString(edge.label)}` : "";
      return [`${head}${label}${tags}${id}`];
    }

    const lines = [`${head}${tags}${id} {`];
    if (edge.label !== undefined) lines.push(`  label ${quoteString(edge.label)}`);
    if (edge.description !== undefined) lines.push(this.renderDescription(edge.description, "  "));
    // Same slot and same canonicalization the node property gets in
    // `renderProperties`: one comma list, before `link`.
    if (edge.facets !== undefined && edge.facets.length > 0) {
      lines.push(`  facets ${edge.facets.map(quoteId).join(", ")}`);
    }
    for (const link of edge.links ?? []) lines.push(this.renderLink(link, "  "));
    lines.push("}");
    return lines;
  }

  // ── DeployBlock ───────────────────────────────────────────────────────────

  private renderDeployBlock(block: DeployBlock): string[] {
    const trail = this.extractTrailing(block.loc.start.line);
    const lines: string[] = [`deploy ${quoteId(block.id)} {${trail}`];

    if (block.label !== undefined) lines.push(`  label ${quoteString(block.label)}`);

    let prevEndLine = block.loc.start.line;
    for (let i = 0; i < block.nodes.length; i++) {
      const node = block.nodes[i];
      const leadingToks = this.extractLeading(prevEndLine + 1, node.loc.start.line - 1);
      if (i > 0 || block.label !== undefined) lines.push("");
      lines.push(...this.renderLeading(leadingToks, "  "));
      lines.push(...this.renderDeployNode(node));
      prevEndLine = node.loc.end.line;
    }

    lines.push("}");
    return lines;
  }

  private renderDeployNode(node: DeployNode): string[] {
    const trail = this.extractTrailing(node.loc.start.line);
    const lines: string[] = [`  ${node.kind} ${quoteId(node.id)} {${trail}`];

    if (node.label !== undefined) lines.push(`    label ${quoteString(node.label)}`);
    if (node.properties.runtime !== undefined)
      lines.push(`    runtime ${quoteString(node.properties.runtime)}`);
    if (node.properties.image !== undefined)
      lines.push(`    image ${quoteString(node.properties.image)}`);
    if (node.properties.type !== undefined)
      lines.push(`    type ${quoteString(node.properties.type)}`);
    if (node.properties.schedule !== undefined)
      lines.push(`    schedule ${quoteString(node.properties.schedule)}`);
    // One target per line, even when the source wrote them comma-separated
    // (#2167): repeated lines are the canonical form the comma list sugars.
    for (const r of node.properties.realizes ?? []) {
      // Path refs print as written; each segment is re-quoted only when
      // needed (TPL-1101, #2088).
      lines.push(`    realizes ${r.path.map(quoteId).join(".")}`);
    }

    lines.push("  }");
    return lines;
  }

  // ── OrganizationBlock ─────────────────────────────────────────────────────

  private renderOrganizationBlock(block: OrganizationBlock): string[] {
    const trail = this.extractTrailing(block.loc.start.line);
    const lines: string[] = [`organization ${quoteId(block.id)} {${trail}`];

    if (block.label !== undefined) lines.push(`  label ${quoteString(block.label)}`);
    if (block.properties.description !== undefined) {
      lines.push(this.renderDescription(block.properties.description, "  "));
    }
    for (const link of block.properties.links) {
      lines.push(this.renderLink(link, "  "));
    }

    let prevEndLine = block.loc.start.line;
    for (let i = 0; i < block.teams.length; i++) {
      const team = block.teams[i];
      const leadingToks = this.extractLeading(prevEndLine + 1, team.loc.start.line - 1);
      if (lines.length > 1) lines.push("");
      lines.push(...this.renderLeading(leadingToks, "  "));
      lines.push(...this.renderTeam(team, 1));
      prevEndLine = team.loc.end.line;
    }

    lines.push("}");
    return lines;
  }

  private renderTeam(team: TeamNode, depth: number): string[] {
    const indent = "  ".repeat(depth);
    const trail = this.extractTrailing(team.loc.start.line);
    const decl = [
      "team",
      quoteId(team.id),
      ...renderAnnotations(team.annotations, team.annotationParams),
    ];
    const lines: string[] = [`${indent}${decl.join(" ")} {${trail}`];

    if (team.label !== undefined) lines.push(`${indent}  label ${quoteString(team.label)}`);
    if (team.properties.description !== undefined) {
      lines.push(this.renderDescription(team.properties.description, `${indent}  `));
    }
    for (const link of team.properties.links) {
      lines.push(this.renderLink(link, `${indent}  `));
    }
    for (const owns of team.properties.owns) {
      // Author notation is preserved segment-for-segment (TPL-1101): a path
      // ref prints as written, each segment re-quoted only if it needs it.
      lines.push(`${indent}  owns ${owns.map(quoteId).join(".")}`);
    }

    let prevEndLine = team.loc.start.line;
    for (const child of team.children) {
      const startLine = child.loc.start.line;
      const endLine = child.loc.end.line;
      const leadingToks = this.extractLeading(prevEndLine + 1, startLine - 1);
      if (lines.length > 1) lines.push("");
      lines.push(...this.renderLeading(leadingToks, `${indent}  `));
      if (child.kind === "team") {
        lines.push(...this.renderTeam(child as TeamNode, depth + 1));
      } else {
        lines.push(...this.renderMember(child as MemberNode, depth + 1));
      }
      prevEndLine = endLine;
    }

    lines.push(`${indent}}`);
    return lines;
  }

  private renderMember(member: MemberNode, depth: number): string[] {
    const indent = "  ".repeat(depth);
    const trail = this.extractTrailing(member.loc.start.line);
    const lines: string[] = [`${indent}member ${quoteId(member.id)} {${trail}`];

    if (member.label !== undefined) lines.push(`${indent}  label ${quoteString(member.label)}`);
    if (member.properties.description !== undefined) {
      lines.push(this.renderDescription(member.properties.description, `${indent}  `));
    }
    if (member.properties.slack !== undefined)
      lines.push(`${indent}  slack ${quoteString(member.properties.slack)}`);
    if (member.properties.github !== undefined)
      lines.push(`${indent}  github ${quoteString(member.properties.github)}`);

    lines.push(`${indent}}`);
    return lines;
  }

  // ── BoundaryBlock ─────────────────────────────────────────────────────────

  private renderBoundaryBlock(block: BoundaryBlock): string[] {
    const trail = this.extractTrailing(block.loc.start.line);
    // The label is emitted as a `label` property rather than in the header
    // position (`boundary g "G" {`). Both parse to the same AST; picking one
    // canonical form keeps the output idempotent.
    const lines: string[] = [`boundary ${quoteId(block.id)} {${trail}`];

    if (block.label !== undefined) lines.push(`  label ${quoteString(block.label)}`);
    if (block.properties.description !== undefined) {
      lines.push(this.renderDescription(block.properties.description, "  "));
    }
    for (const link of block.properties.links) {
      lines.push(this.renderLink(link, "  "));
    }
    for (const ref of block.contains) {
      // Same round-trip rule as `owns` (TPL-1101): print the path as written.
      lines.push(`  contains ${ref.map(quoteId).join(".")}`);
    }

    lines.push("}");
    return lines;
  }

  // ── FacetBlock ────────────────────────────────────────────────────────────

  private renderFacetBlock(block: FacetBlock): string[] {
    const trail = this.extractTrailing(block.loc.start.line);
    const lines: string[] = [`facet ${quoteId(block.id)} {${trail}`];

    if (block.label !== undefined) lines.push(`  label ${quoteString(block.label)}`);
    if (block.properties.description !== undefined) {
      lines.push(this.renderDescription(block.properties.description, "  "));
    }
    for (const link of block.properties.links) {
      lines.push(this.renderLink(link, "  "));
    }

    lines.push("}");
    return lines;
  }

  // ── LegendBlock ───────────────────────────────────────────────────────────

  private renderLegendBlock(block: LegendBlock): string[] {
    const trail = this.extractTrailing(block.loc.start.line);
    const decl = ["legend"];
    if (block.scope !== undefined) decl.push(block.scope);
    if (block.title !== undefined) decl.push(`${quoteString(block.title)}`);

    const lines: string[] = [`${decl.join(" ")} {${trail}`];
    for (const entry of block.entries) {
      lines.push(`  ${this.renderLegendEntry(entry)}`);
    }
    lines.push("}");
    return lines;
  }

  private renderLegendEntry(entry: LegendEntry): string {
    if (entry.kind === "swatch") {
      // `color` is stored verbatim including the leading `#` (the lexer emits
      // `#xxx` as one token), so it round-trips as-is.
      return `swatch ${entry.color} ${quoteString(entry.label)}`;
    }
    return `ref ${renderLegendRefTarget(entry.target)} ${quoteString(entry.label)}`;
  }
}

/**
 * Emit an annotation list, parameters included.
 *
 * Shared by `renderNode` and `renderTeam` because both hosts carry
 * `annotationParams` and both lost them before #2571: the node axis dropped
 * the parameters, the team axis dropped the annotation whole. That is the
 * "parsed, then silently discarded" break TPL-1101 / ADR-2076 name.
 *
 * A parameter value reaches the AST as a bare string, so the author's choice
 * of quoting is not recoverable and one canonical spelling has to be picked
 * per value kind (see `AnnotationParamKind`). Key order follows the parse
 * order `annotationParams` holds, so the order the author wrote survives.
 */
function renderAnnotations(
  annotations: string[],
  annotationParams: Record<string, Record<string, string>> | undefined,
): string[] {
  // `annotationParams` is keyed by annotation name, so a name written twice
  // shares one entry and the AST cannot say which occurrence held it. Both
  // occurrences therefore print it. That widens `@deprecated
  // @deprecated(until: "x")` to two parameterized copies, which is the only
  // reading that keeps the AST intact: emitting the second one bare would
  // delete the parameter, the very break this function exists to fix.
  return annotations.map((name) => {
    const entries = Object.entries(annotationParams?.[name] ?? {});
    if (entries.length === 0) return `@${name}`;
    const params = entries.map(
      ([key, value]) => `${key}: ${renderAnnotationParam(name, key, value)}`,
    );
    return `@${name}(${params.join(", ")})`;
  });
}

/** Quote one annotation parameter value by the kind the parser recorded for it. */
function renderAnnotationParam(annotation: string, key: string, value: string): string {
  // Only recognized keys reach the AST (the parser warns on and drops the
  // rest), so the lookup hits. The `string` fallback keeps a value that
  // somehow arrived unrecognized parseable rather than emitting it bare.
  return annotationParamKind(annotation, key) === "ref" ? quoteId(value) : quoteString(value);
}

function renderLegendRefTarget(target: LegendRefTarget): string {
  switch (target.kind) {
    case "annotation":
      return `@${target.name}`;
    case "tag":
      return `[${target.name}]`;
    case "selector":
      // Selectors keep their sigil (`.foo`, `#Node`) or are a bare type name.
      return target.selector;
  }
}
